import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSetupProgress } from "@/hooks/useSetupProgress";
import { useVehicleMetrics } from "@/hooks/useVehicleMetrics";
import { ArrowRight, Lightbulb } from "lucide-react";
import { Link } from "react-router-dom";

interface NextAction {
  title: string;
  why: string;
  progress: string;
  href: string;
  cta: string;
}

function getNextAction(
  steps: { key: string; completed: boolean }[],
  vehicleCount: number,
  tripCount: number,
  expenseCount: number,
): NextAction {
  const incomplete = steps.find((s) => !s.completed);

  if (incomplete) {
    switch (incomplete.key) {
      case "vehicle":
        return {
          title: "Add your first vehicle",
          why: "HostCaptain needs at least one vehicle to track your performance.",
          progress: "This gets you closer to knowing if hosting is worth it.",
          href: "/app/vehicles",
          cta: "Add a Vehicle",
        };
      case "trip":
        return {
          title: "Enter earnings for at least one trip",
          why: "Without earnings data, we can't tell you whether you're making money.",
          progress: "This gets you closer to seeing your first profit number.",
          href: "/app/earnings",
          cta: "Enter Earnings",
        };
      case "expense":
        return {
          title: "Enter at least one expense",
          why: "Knowing what you spend is just as important as what you earn.",
          progress: "This gets you closer to a real profit picture.",
          href: "/app/expenses",
          cta: "Add an Expense",
        };
      case "assign":
        return {
          title: "Assign an expense to a vehicle",
          why: "Assigning expenses lets you see profitability per vehicle, not just overall.",
          progress: "This gets you closer to knowing which car is worth it.",
          href: "/app/expenses",
          cta: "Go to Expenses",
        };
      case "snapshot":
        return {
          title: "Complete your first profit snapshot",
          why: "You have data in the system — now connect the dots.",
          progress: "This gets you your first real answer: am I making money?",
          href: "/app/orientation/signals",
          cta: "View Signals",
        };
    }
  }

  // All setup complete — suggest deeper exploration
  if (expenseCount < 5) {
    return {
      title: `Enter ${Math.max(1, 5 - expenseCount)} more expenses to see if this car is profitable`,
      why: "A few expenses won't give the full picture. The more you log, the more accurate your P&L becomes.",
      progress: "This gets you closer to a confident answer on profitability.",
      href: "/app/expenses",
      cta: "Add Expenses",
    };
  }

  if (vehicleCount < 2) {
    return {
      title: "Add a second vehicle to compare performance",
      why: "Comparing vehicles helps you decide where to invest more — or less.",
      progress: "This gets you closer to knowing which cars are pulling their weight.",
      href: "/app/vehicles",
      cta: "Add a Vehicle",
    };
  }

  return {
    title: "Keep logging trips and expenses",
    why: "Consistent tracking is what separates successful hosts from everyone else.",
    progress: "The longer you track, the better your insights become.",
    href: "/app/earnings",
    cta: "Enter Earnings",
  };
}

export default function GuidanceNextSteps() {
  const { steps, allComplete, isLoading: setupLoading } = useSetupProgress();
  const { vehicleMetrics, isLoading: metricsLoading } = useVehicleMetrics();

  const isLoading = setupLoading || metricsLoading;

  const tripCount = 0; // approximate — we derive from steps
  const expenseCount = 0;
  const action = getNextAction(steps, vehicleMetrics?.length || 0, tripCount, expenseCount);

  return (
    <AppLayout>
      <div className="p-4 md:p-8 space-y-6 max-w-2xl">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Next Steps</h1>
          <p className="text-muted-foreground mt-1">
            Here's exactly what to do next to make progress.
          </p>
        </div>

        {isLoading ? (
          <Skeleton className="h-48" />
        ) : (
          <>
            {/* Primary action card */}
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-start gap-3">
                  <Lightbulb className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="space-y-2">
                    <h2 className="text-lg font-semibold text-foreground">{action.title}</h2>
                    <p className="text-sm text-muted-foreground">{action.why}</p>
                    <p className="text-sm italic text-muted-foreground">{action.progress}</p>
                  </div>
                </div>
                <Button asChild className="w-full">
                  <Link to={action.href}>
                    {action.cta}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Subtle upgrade teaser — only after setup is complete */}
            {allComplete && (
              <p className="text-xs text-muted-foreground text-center">
                With more vehicles, HostCaptain can help you compare and prioritize.{" "}
                <Link to="/app/upgrade" className="text-primary underline underline-offset-2">
                  Learn more
                </Link>
              </p>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
